import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import * as Sentry from "@sentry/angular";
import { finalize, retry } from "rxjs/operators";
import { BlockingUiSignalService } from "./blocking-ui-signal.service";
import { AppUpdatesApiService } from "./app-updates-api.service";
import { DialogUpdateCompleteComponent } from "../common/dialog-refresh/dialog-update-complete.component";

/**
 * Shows the "you're up to date! / what's new" modal (FLIP-79) exactly once per app
 * version per user, on the first session actually running that version.
 *
 * Covers all three arrival routes the ticket lists - Update now, a snooze re-show, and
 * a fresh open with no prior prompt - from one call site: the first two both end in a
 * real `location.reload()`, which is indistinguishable at boot time from the third, so
 * there's nothing route-specific to hook.
 *
 * "Seen" is tracked server-side (FLIP-81's UserAppUpdateStatuses table, via
 * GET /api/app-updates/latest / POST /api/app-updates/mark-seen), not localStorage - the
 * ticket rules that out as unreliable across a mobile reinstall. GetUnseenAsync already
 * excludes anything this user has been shown, so there's no client-side flag-key
 * bookkeeping needed here at all: a non-empty response IS "show it".
 *
 * A user can be behind by more than one live announcement (FLIP-332) - GetUnseenAsync
 * returns all of them, oldest first, and this service accumulates them into one showing
 * rather than one modal per skipped version, marking seen once at the highest version.
 */
@Injectable({ providedIn: "root" })
export class UpdateCompleteModalService {
  constructor(
    private dialog: MatDialog,
    private appUpdatesApiService: AppUpdatesApiService,
    private blockingUiSignal: BlockingUiSignalService
  ) {}

  /**
   * Fetches every unseen live announcement and shows the modal if there's at least one.
   */
  checkAndMaybeShow(): void {
    // Blocking until the fetch settles, so onboarding doesn't open first (FLIP-334).
    const releasePendingCheck = this.blockingUiSignal.registerPendingCheck();
    this.appUpdatesApiService.getLatest().pipe(finalize(releasePendingCheck)).subscribe({
      next: (response) => {
        const data = response?.responseData;
        // Array.isArray also rejects a pre-FLIP-332 single-object response (an API still on
        // the old shape), which has no .length and would otherwise be treated as "empty".
        const announcements = Array.isArray(data) ? data : [];
        // Never open a blank modal: drop entries with no version, and entries with nothing
        // to list. When something is shown, mark-seen still uses the highest valid version,
        // content-less ones included.
        const valid = announcements.filter((a) => !!a?.version);
        const withContent = valid.filter(
          (a) => (a.newFeatures?.length ?? 0) + (a.bugFixes?.length ?? 0) > 0
        );
        if (!withContent.length) {
          // Nothing to show for this user - clean no-op.
          return;
        }
        this.show(withContent, valid[valid.length - 1].version);
      },
      // Non-critical: skip showing the modal this time rather than risk it never
      // showing at all if this particular fetch fails.
      error: () => {},
    });
  }

  private show(
    announcements: {
      version: string;
      newFeatures: string[];
      bugFixes: string[];
      requiresIosUpdate?: boolean;
      requiresAndroidUpdate?: boolean;
    }[],
    latestVersion: string
  ): void {
    // latestVersion is the highest version GetUnseenAsync returned (oldest-first), used for
    // the modal's header and for the single mark-seen call below.
    const dialogRef = this.dialog.open(DialogUpdateCompleteComponent, {
      panelClass: "base-modal-panel",
      backdropClass: "base-modal-backdrop",
      data: {
        latestVersion,
        // FLIP-563: per platform, across every announcement being shown - a skipped release
        // that needed the mobile update still applies to a user who is behind by several.
        requiresIosUpdate: announcements.some((a) => a.requiresIosUpdate === true),
        requiresAndroidUpdate: announcements.some((a) => a.requiresAndroidUpdate === true),
        announcements: announcements.map((a) => ({
          version: a.version,
          newFeatures: a.newFeatures ?? [],
          bugFixes: a.bugFixes ?? [],
        })),
      },
    });

    // Marked seen once the modal is actually closed (any dismissal route - action button,
    // backdrop click, Escape - all resolve afterClosed()), not fired-and-forgotten the
    // instant it opens. Firing on open meant a closed/backgrounded tab right after render
    // could drop the request before it ever left the browser, with nothing server-side to
    // even show it was attempted - GetUnseenAsync would then find no "seen" row on the
    // next session and show the modal again, indefinitely.
    //
    // Still not bulletproof (the tab can close between afterClosed() firing and the request
    // completing), but it moves the write past the point where the user has engaged with
    // the modal rather than racing its own render. One retry covers the common transient
    // case; a still-failing write is reported so it's at least visible instead of silently
    // repeating. A single call with the highest version marks every accumulated version
    // seen at once (MarkSeenAsync is monotonic), rather than one call per skipped version.
    dialogRef.afterClosed().subscribe(() => {
      this.appUpdatesApiService
        .markSeen(latestVersion)
        .pipe(retry(1))
        .subscribe({
          error: (err) => Sentry.captureException(err, { extra: { version: latestVersion } }),
        });
    });
  }
}
